import { useState, useEffect, useCallback, useRef } from "react";
import { api, apiErr } from "@/lib/api";
import { fmtTime } from "@/lib/format";
import { toast } from "sonner";
import { HardDriveDownload, ShieldAlert, RotateCw, RefreshCw, Check, X, Loader2 } from "lucide-react";

// Control plane OTA de l'Infra API elle-même. DISTINCT de Gateway OTA et Agent OTA.
// Étapes = BACKEND_STEPS + DEVICE_STEPS côté backend infra_ota.py. Aucune calculée côté client.
const JOB_STEPS = [
  "PRECHECK", "RELEASE_RESOLVE", "PUBLICATION_VERIFY", "LOCK", "DISPATCH",
  "FETCH", "VERIFY_MANIFEST", "VERIFY_GIT_SHA", "VERIFY_SOURCE_SHA", "BACKUP",
  "INSTALL", "COMPILE", "RESTART", "HEALTH", "WRITE_RELEASE", "UPDATE_INDEX",
];

const TERMINAL = ["success", "failed", "rolled_back", "rollback_failed", "result_unknown"];

const CHECK_LABELS = {
  infra_api: "Infra API joignable",
  infra_health: "Infra API saine",
  release_available: "Release disponible",
  publication_verified: "Publication immuable vérifiée",
  no_ota_job: "Aucun job Infra actif",
};
const CHECK_ORDER = Object.keys(CHECK_LABELS);

function CheckRow({ ok, label, testid }) {
  return (
    <div data-testid={testid} data-ok={ok ? "true" : "false"}
      className="flex items-center gap-2 font-mono text-[11px] py-1">
      {ok ? <Check size={13} className="text-[#22C55E] shrink-0" /> : <X size={13} className="text-[#EF4444] shrink-0" />}
      <span className={ok ? "text-[#D4D4D4]" : "text-[#EF4444]"}>{label}</span>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-[#0F0F0F] border border-white/10 rounded-sm p-3">
      <div className="text-[9px] uppercase text-[#737373] tracking-wider mb-1">{label}</div>
      <div className="font-mono text-[11px] text-white break-all">{value ?? "—"}</div>
    </div>
  );
}

export default function InfraApiOtaTab() {
  const [pf, setPf] = useState(null);
  const [pfErr, setPfErr] = useState(null);
  const [releases, setReleases] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [p, r, h] = await Promise.all([
      api.get("/infra-ota/preflight").then((x) => x.data).catch((e) => ({ __err: apiErr(e) })),
      api.get("/infra-ota/releases").then((x) => x.data).catch(() => ({ releases: [] })),
      api.get("/infra-ota/jobs", { params: { limit: 20 } }).then((x) => x.data).catch(() => ({ jobs: [] })),
    ]);
    if (p.__err) { setPfErr(p.__err); setPf(null); } else { setPf(p); setPfErr(null); }
    setReleases(r.releases || []);
    setHistory(h.jobs || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Polling du job. AUCUN second POST automatique (RESULT_UNKNOWN inclus).
  const pollJob = useCallback((jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/infra-ota/jobs/${jobId}`);
        setJob(data);
        if (TERMINAL.includes(data.status)) {
          clearInterval(pollRef.current); pollRef.current = null;
          loadAll();
          if (data.status === "success") toast.success("Mise à jour Infra API réussie");
          else if (data.status === "result_unknown") toast.warning("Résultat incertain — aucun redéploiement automatique");
          else toast.error(`Job Infra terminé : ${data.status}`);
        }
      } catch { /* on continue de poller */ }
    }, 2000);
  }, [loadAll]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const runMutation = async () => {
    if (!confirm) return;
    setBusy(true);
    const kind = confirm.kind;
    const idemKey = `infra-${kind}-${Date.now()}`;
    try {
      const url = kind === "update" ? "/infra-ota/deploy" : "/infra-ota/rollback";
      // Le navigateur n'envoie QUE le release_id (jamais de path/sha/ref).
      const body = kind === "update" ? { release: confirm.target } : {};
      const { data } = await api.post(url, body, { headers: { "X-OTA-Key": idemKey } });
      setConfirm(null);
      setJob({ id: data.job_id, status: data.status || "queued", steps: [] });
      pollJob(data.job_id);
      toast.info("Job Infra API lancé via l'exécuteur externe");
    } catch (e) {
      toast.error(apiErr(e, "Lancement du job Infra impossible"));
    } finally {
      setBusy(false);
    }
  };

  const readyUpdate = pf?.ready_update === true;      // dérivé UNIQUEMENT du backend
  const readyRollback = pf?.ready_rollback === true;
  const checksByKey = Object.fromEntries((pf?.checks || []).map((c) => [c.key, c.ok]));
  const jobActive = job && !TERMINAL.includes(job.status);

  return (
    <div className="space-y-4 max-w-3xl" data-testid="infra-ota-panel">
      <div className="bg-[#141414] border border-white/10 rounded-sm p-5 space-y-4" data-testid="infra-ota-card">
        <div className="flex items-center gap-2">
          <HardDriveDownload size={16} className="text-[#3B82F6]" />
          <span className="font-chivo font-black uppercase tracking-tight">Infra API — Déploiement OTA (self-update)</span>
          <button data-testid="infra-ota-refresh" onClick={loadAll}
            className="ml-auto flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-2 py-1 hover:bg-white/10">
            <RefreshCw size={11} /> Rafraîchir
          </button>
        </div>

        <div data-testid="infra-ota-warning" className="flex items-start gap-2 bg-[#F59E0B]/10 border border-[#F59E0B]/40 rounded-sm px-4 py-3">
          <ShieldAlert size={16} className="text-[#F59E0B] shrink-0 mt-0.5" />
          <div className="font-mono text-[11px] text-[#F59E0B]">
            <span className="font-bold uppercase mr-2 border border-[#F59E0B]/50 rounded px-1">Supervision</span>
            Cette opération redémarre l'Infra API ; la supervision Infrastructure est temporairement indisponible. Le déploiement se poursuit via l'exécuteur externe. <b>La Gateway, les drones et le Flight Control ne sont PAS redémarrés.</b>
          </div>
        </div>

        {pfErr && (
          <div data-testid="infra-ota-pf-error" className="font-mono text-[11px] text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-sm px-3 py-2">
            Préflight indisponible : {pfErr}. Boutons désactivés (fail-closed).
          </div>
        )}

        <div data-testid="infra-ota-preflight" className="bg-[#0F0F0F] border border-white/10 rounded-sm p-3">
          <div className="text-[9px] uppercase text-[#737373] tracking-wider mb-2 flex items-center gap-2">
            Pré-flight OTA
            {loading && <Loader2 size={11} className="animate-spin text-[#737373]" />}
            {pf?.checked_at && <span className="ml-auto normal-case tracking-normal" data-testid="infra-ota-checked-at">Vérifié : {fmtTime(pf.checked_at)}</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            {CHECK_ORDER.map((k) => (
              <CheckRow key={k} ok={!!checksByKey[k]} label={CHECK_LABELS[k]} testid={`infra-ota-check-${k}`} />
            ))}
            <CheckRow ok={pf?.lineage_ok === true} label={`Lignage validé${pf?.expected_previous_release ? ` (attendu : ${pf.expected_previous_release})` : ""}`} testid="infra-ota-check-lineage" />
            <CheckRow ok={pf?.rollback_available === true} label="Rollback disponible" testid="infra-ota-check-rollback" />
          </div>
          {pf && (
            <div data-testid="infra-ota-verdict" data-verdict={pf.verdict || (readyUpdate ? "GO" : "NO-GO")}
              className={`mt-2 font-mono text-[11px] font-bold ${readyUpdate ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
              Verdict installation : {readyUpdate ? "GO" : "NO-GO"}
              {!readyUpdate && pf.reasons?.length ? <span className="font-normal text-[#A3A3A3]"> — {pf.reasons.join(", ")}</span> : null}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Release installée" value={pf?.installed_release} testid="infra-ota-installed" />
          <Field label="Git SHA installé" value={pf?.installed_git_sha ? pf.installed_git_sha.slice(0, 12) : null} testid="infra-ota-installed-sha" />
          <Field label="Release disponible" value={pf?.available_release} testid="infra-ota-available" />
          <Field label="Git SHA source" value={pf?.available_git_sha ? pf.available_git_sha.slice(0, 12) : null} testid="infra-ota-available-sha" />
          <Field label="Publication SHA" value={pf?.publication_git_sha ? pf.publication_git_sha.slice(0, 12) : null} testid="infra-ota-publication-sha" />
          <Field label="Previous known good" value={pf?.previous_known_good} testid="infra-ota-pkg" />
          <Field label="Job actif" value={pf?.active_job ? `${pf.active_job.id} (${pf.active_job.status})` : "Aucun"} testid="infra-ota-active-job" />
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <button data-testid="infra-ota-update" disabled={!readyUpdate || busy || jobActive}
            onClick={() => setConfirm({ kind: "update", current: pf?.installed_release, target: pf?.available_release })}
            title={readyUpdate ? "" : "Préflight non validé (fail-closed)"}
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-3 py-2 hover:bg-[#3B82F6]/85 disabled:opacity-40 disabled:cursor-not-allowed">
            <RotateCw size={12} /> Mettre à jour
          </button>
          <button data-testid="infra-ota-rollback" disabled={!readyRollback || busy || jobActive}
            onClick={() => setConfirm({ kind: "rollback", current: pf?.installed_release, target: pf?.previous_known_good })}
            title={readyRollback ? "" : "Rollback indisponible (fail-closed)"}
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider border border-[#F59E0B]/40 text-[#F59E0B] rounded-sm px-3 py-2 hover:bg-[#F59E0B]/10 disabled:opacity-40 disabled:cursor-not-allowed">
            <RotateCw size={12} /> Rollback
          </button>
          {!readyUpdate && !readyRollback && !loading && (
            <span data-testid="infra-ota-disabled-hint" className="font-mono text-[10px] text-[#525252] self-center">
              Actions verrouillées par le préflight backend (fail-closed).
            </span>
          )}
        </div>
      </div>

      {job && (
        <div className="bg-[#141414] border border-white/10 rounded-sm p-4" data-testid="infra-ota-job">
          <div className="text-[9px] uppercase text-[#737373] tracking-wider mb-2 flex items-center gap-2">
            Job {job.id} — <span data-testid="infra-ota-job-status" className="text-white">{job.status}</span>
            {jobActive && <Loader2 size={11} className="animate-spin text-[#3B82F6]" />}
          </div>
          {job.status === "result_unknown" && (
            <div data-testid="infra-ota-result-unknown" className="font-mono text-[11px] text-[#F59E0B] mb-2">
              Résultat incertain — état Infra non confirmé. Aucun redéploiement automatique ; réconciliation read-only effectuée.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            {JOB_STEPS.map((name) => {
              const s = (job.steps || []).find((x) => x.name === name);
              const st = s?.status || "pending";
              const col = st === "success" ? "#22C55E" : st === "failed" ? "#EF4444"
                : st === "running" ? "#3B82F6" : st === "skipped" ? "#525252" : "#3F3F3F";
              return (
                <div key={name} data-testid={`infra-ota-step-${name}`} className="flex items-center gap-2 font-mono text-[10px] py-0.5">
                  <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: col }} />
                  <span className="text-[#A3A3A3]">{name}</span>
                  <span className="ml-auto uppercase" style={{ color: col }}>{st}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-[#141414] border border-white/10 rounded-sm p-4" data-testid="infra-ota-history">
        <div className="text-[9px] uppercase text-[#737373] tracking-wider mb-2">Historique déploiements Infra API</div>
        {history.length === 0 ? (
          <div className="font-mono text-[11px] text-[#525252]">Aucun déploiement Infra.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left font-mono text-[10px]">
              <thead className="text-[#737373] uppercase tracking-wider">
                <tr>
                  <th className="py-1 pr-3">Date</th><th className="pr-3">Type</th><th className="pr-3">Release</th>
                  <th className="pr-3">git_sha</th><th className="pr-3">publication</th><th className="pr-3">Statut</th><th>Par</th>
                </tr>
              </thead>
              <tbody>
                {history.map((j) => (
                  <tr key={j.id} data-testid={`infra-ota-history-row-${j.id}`} className="border-t border-white/5">
                    <td className="py-1 pr-3 text-[#A3A3A3]">{fmtTime(j.created_at)}</td>
                    <td className="pr-3">{j.kind}</td>
                    <td className="pr-3 text-white">{j.release || "—"}</td>
                    <td className="pr-3 text-[#737373]">{j.git_sha ? j.git_sha.slice(0, 10) : "—"}</td>
                    <td className="pr-3 text-[#737373]">{j.publication_git_sha ? j.publication_git_sha.slice(0, 10) : "—"}</td>
                    <td className="pr-3">
                      {j.status === "result_unknown"
                        ? <span data-testid={`infra-ota-history-unknown-${j.id}`} className="text-[#F59E0B]">RESULT_UNKNOWN</span>
                        : <span className={j.status === "success" ? "text-[#22C55E]" : ["failed", "rollback_failed"].includes(j.status) ? "text-[#EF4444]" : "text-[#A3A3A3]"}>{j.status}</span>}
                    </td>
                    <td className="text-[#737373]">{j.created_by_email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 p-4" data-testid="infra-ota-confirm">
          <div className="bg-[#141414] border border-white/15 rounded-sm p-5 max-w-md w-full space-y-3">
            <div className="flex items-center gap-2 font-chivo font-black uppercase tracking-tight">
              <ShieldAlert size={16} className="text-[#F59E0B]" />
              {confirm.kind === "update" ? "Confirmer la mise à jour Infra API" : "Confirmer le rollback Infra API"}
            </div>
            <div className="font-mono text-[11px] text-[#D4D4D4] space-y-1.5">
              <div>Release actuelle : <b className="text-white" data-testid="infra-ota-confirm-current">{confirm.current || "—"}</b></div>
              <div>
                {confirm.kind === "update" ? "Release cible : " : "Retour vers (previous_known_good) : "}
                <b className="text-white" data-testid="infra-ota-confirm-target">{confirm.target || "—"}</b>
              </div>
              <ul className="list-disc list-inside text-[#F59E0B] pt-1 space-y-0.5">
                <li>Redémarre l'Infra API ; supervision Infrastructure temporairement indisponible</li>
                <li>Le déploiement continue via l'exécuteur externe</li>
                <li><b>La Gateway, les drones et le Flight Control ne sont pas redémarrés</b></li>
              </ul>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button data-testid="infra-ota-confirm-cancel" onClick={() => setConfirm(null)} disabled={busy}
                className="font-mono text-[10px] uppercase tracking-wider border border-white/15 rounded-sm px-3 py-2 hover:bg-white/10 disabled:opacity-40">Annuler</button>
              <button data-testid="infra-ota-confirm-yes" onClick={runMutation} disabled={busy}
                className="font-mono text-[10px] uppercase tracking-wider bg-[#3B82F6] hover:bg-[#3B82F6]/85 rounded-sm px-4 py-2 disabled:opacity-50 flex items-center gap-1.5">
                {busy && <Loader2 size={11} className="animate-spin" />} Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
